import api from './api';

const asError = (err, fallback = 'Request failed') => {
  const msg =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;
  return new Error(msg);
};

export const fetchAdminBookings = async (params = {}) => {
  try {
    const res = await api.get('/booking-v1/admin/bookings', { params });
    return res.data || { items: [], total: 0 };
  } catch (err) {
    throw asError(err, 'Failed to fetch bookings');
  }
};

export const fetchAdminBooking = async (bookingId) => {
  try {
    const res = await api.get(`/booking-v1/admin/bookings/${bookingId}`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to fetch booking');
  }
};

export const confirmAdminBooking = async (bookingId, note) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/confirm`, note ? { note } : {});
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to confirm booking');
  }
};

export const markAdminBookingPaid = async (bookingId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/payment/mark-paid`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to mark booking as paid');
  }
};

export const markAdminBookingPaymentPending = async (bookingId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/payment/mark-pending`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to mark booking payment as pending');
  }
};

export const markAdminBookingPaymentFailed = async (bookingId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/payment/mark-failed`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to mark booking payment as failed');
  }
};

export const createAdminBookingPaymentLink = async (bookingId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/payment/create-link`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to create payment link');
  }
};

export const rejectAdminBooking = async (bookingId, reason) => {
  try {
    const res = await api.post(`/booking-v1/admin/bookings/${bookingId}/reject`, { reason });
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to reject booking');
  }
};

export const createAdminBookingParent = async (payload = {}) => {
  try {
    const res = await api.post('/booking-v1/admin/parents', payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to create booking parent');
  }
};

export const updateAdminBookingParent = async (parentId, payload = {}) => {
  try {
    const res = await api.put(`/booking-v1/admin/parents/${parentId}`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to update booking parent');
  }
};

export const changeAdminBookingParentPassword = async (parentId, newPassword) => {
  try {
    const res = await api.post(`/booking-v1/admin/parents/${parentId}/change-password`, { newPassword });
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to change parent password');
  }
};

export const resendAdminBookingParentWelcome = async (parentId, password) => {
  try {
    const res = await api.post(`/booking-v1/admin/parents/${parentId}/resend-welcome`, password ? { password } : {});
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to resend parent welcome email');
  }
};

export const lockAdminBookingParent = async (parentId) => {
  try {
    const res = await api.patch(`/booking-v1/admin/parents/${parentId}/lock`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to lock parent account');
  }
};

export const unlockAdminBookingParent = async (parentId) => {
  try {
    const res = await api.patch(`/booking-v1/admin/parents/${parentId}/unlock`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to unlock parent account');
  }
};

export const deactivateAdminBookingParent = async (parentId) => {
  try {
    const res = await api.patch(`/booking-v1/admin/parents/${parentId}/deactivate`);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to deactivate parent account');
  }
};

export const createAdminBookingStudent = async (parentId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/parents/${parentId}/students`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to create booking student');
  }
};

export const fetchAdminBookingStudentPackages = async (studentId) => {
  try {
    const res = await api.get(`/booking-v1/admin/students/${studentId}/packages`);
    return res.data || { packages: [] };
  } catch (err) {
    throw asError(err, 'Failed to fetch booking packages');
  }
};

export const fetchAdminLinkableLmsStudents = async (params = {}) => {
  try {
    const res = await api.get('/booking-v1/admin/lms-students/linkable', { params });
    return res.data || { items: [], total: 0 };
  } catch (err) {
    throw asError(err, 'Failed to fetch linkable LMS students');
  }
};

export const linkAdminLmsStudentToBookingParent = async (parentId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/parents/${parentId}/link-lms-student`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to link LMS student');
  }
};

export const assignAdminBookingStudentPackage = async (studentId, payload = {}) => {
  try {
    const res = await api.post(`/booking-v1/admin/students/${studentId}/package`, payload);
    return res.data;
  } catch (err) {
    throw asError(err, 'Failed to assign booking package');
  }
};
